import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../config/supabase';
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null), [access, setAccess] = useState(null), [loading, setLoading] = useState(true);
  const refresh = useRef(() => {});
  useEffect(() => {
    let active = true, generation = 0, authEventSeen = false, verifiedId = null;
    const applySession = async (session) => {
      const request = ++generation;
      if (!session?.access_token || session.user?.is_anonymous) {
        verifiedId = null; setUser(null); setAccess(null); setLoading(false); return;
      }
      if (verifiedId !== session.user.id) { setUser(null); setAccess(null); setLoading(true); }
      try {
        const { data, error } = await supabase.auth.getUser(session.access_token);
        if (!active || request !== generation) return;
        if (error || !data.user || data.user.is_anonymous) throw new Error('Invalid session');
        const [result, allowed] = await Promise.all([
          supabase.from('admin_access').select('role, active').eq('user_id', data.user.id).maybeSingle(),
          supabase.rpc('has_studio_access')
        ]);
        if (!active || request !== generation) return;
        verifiedId = data.user.id;
        setUser(data.user); setAccess(result.error || allowed.error || !allowed.data ? null : result.data); setLoading(false);
      } catch {
        if (active && request === generation) { verifiedId = null; setUser(null); setAccess(null); setLoading(false); }
      }
    };
    const verify = () => supabase.auth.getSession().then(({ data, error }) => {
      if (active) return applySession(error ? null : data.session);
    }).catch(() => { if (active) applySession(null); });
    refresh.current = verify;
    supabase.auth.getSession().then(({ data, error }) => { if (active && !authEventSeen) applySession(error ? null : data.session); })
      .catch(() => { if (active && !authEventSeen) applySession(null); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      authEventSeen = true;
      // Defer network calls until the auth callback releases its session lock.
      queueMicrotask(() => { if (active) void applySession(session); });
    });
    window.addEventListener('focus', verify);
    const timer = setInterval(verify, 60000);
    return () => { active = false; generation++; subscription.unsubscribe(); clearInterval(timer); window.removeEventListener('focus', verify); };
  }, []);
  const profile = user ? { id: user.id, username: user.user_metadata?.username || '', full_name: user.user_metadata?.full_name || '',
    email: user.email, is_admin: !!access?.active, is_super_admin: access?.active && access.role === 'manager' } : null;
  const canAccessStudio = !!user && !user.is_anonymous && !!access?.active;
  const canManageUsers = canAccessStudio && access.role === 'manager';
  const updateProfile = async ({ full_name, username }) => {
    const result = await supabase.auth.updateUser({ data: { full_name, username } });
    if (result.data?.user) setUser(result.data.user);
    return result;
  };
  return <AuthContext.Provider value={{ user, profile, access, loading, canAccessStudio, canManageUsers,
    isAdmin: canAccessStudio, isSuperAdmin: canManageUsers,
    signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
    signOut: async () => { const result = await supabase.auth.signOut(); if (!result.error) { setUser(null); setAccess(null); } return result; },
    updateProfile, refreshAccess: () => refresh.current(), incrementClickCount: async () => {} }}>{children}</AuthContext.Provider>;
};
